import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { BiSend } from 'react-icons/bi';
import Logo from '../../assets/favicon.png';
import { format } from 'date-fns';
import apiUtils from 'utils/apiutils';
import { FaUser } from 'react-icons/fa6';

const TypingIndicator = () => (
  <div className="flex items-center gap-2 px-4 py-3">
    <div className="flex space-x-2">
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut", delay: 0 }}
        className="w-2 h-2 bg-gray-400 rounded-full"
      />
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut", delay: 0.2 }}
        className="w-2 h-2 bg-gray-400 rounded-full"
      />
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut", delay: 0.4 }}
        className="w-2 h-2 bg-gray-400 rounded-full"
      />
    </div>
    <span className="text-sm text-gray-500">Bot is typing...</span>
  </div>
);

const ChatInterface = ({ profileData }) => {
  const [messages, setMessages] = useState([
    {
      sender: 'bot',
      content:
        "👋 Welcome to Profitsla Assistant!\n\nYou are interacting with our secure automated bot, built exclusively to answer Profitsla-related questions.\n\n🔐 Security & Privacy Assurance:\n• Responds only to Profitsla platform queries\n• No data is stored or remembered by the AI\n• All conversations use privacy-filtered context\n• Protected against prompt injection and misuse\n\n📊 Supported Topics:\n• Trading bots & automated strategies\n• Backtesting tools & performance analysis\n• Calculators & trading utilities\n• Exchange integrations & connectivity\n• Account settings & management\n• Subscription plans & billing\n\n📌 For quicker assistance, please keep your account details and exchange information ready.\n",
      timestamp: new Date(),


    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const renderFormattedText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);

    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <span key={index} className="font-bold text-secondary">
            {part.replace(/\*\*/g, '')}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      sender: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await apiUtils.post('/chatbot/chat', {
        message: input.trim(),
        context_limit: 10000,
      });

      const botReply = response?.Success?.response;

      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          content: botReply,
          timestamp: new Date(),
        },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          content: "Sorry, something went wrong. Please try again later.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>

      <div className="flex flex-col h-full">
        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4 ">
          {messages.map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex  ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex gap-3 max-w-[85%] ${msg.sender === 'user' ? 'flex-row-reverse' : ''}`}>
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {msg.sender === 'bot' ? (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-r from-primary to-black flex items-center justify-center text-white font-bold text-sm">
                      <img src={Logo} alt="" />

                    </div>
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-primary text-black flex items-center justify-center  font-bold text-sm">
                      {profileData?.Profile?.Email?.split('@')[0]?.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div>
                  <div
                    className={`px-4 py-3 rounded-2xl shadow-sm ${msg.sender === 'user'
                      ? 'bg-primary text-black rounded-tr-none'
                      : 'bg-gray-100 text-gray-800 rounded-tl-none'
                      }`}
                  >
                    {msg.image && msg.sender === 'bot' && (
                      <img src={FaUser} className="rounded-lg mb-3 max-w-full" />
                    )}
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">
                      {renderFormattedText(msg.content)}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 px-1">
                    {format(msg.timestamp, 'h:mm a')}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}

          {isLoading && <TypingIndicator />}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-primary  p-4 shrink-0">
          <div className="flex items-center gap-3 bg-gray-100 rounded-full px-5 py-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Please enter your query"
              className="flex-1 bg-transparent outline-none text-gray-700 placeholder-gray-500"
              disabled={isLoading}
            />
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={sendMessage}
              disabled={isLoading || !input.trim()}
              className={`p-2 rounded-full transition ${input.trim() && !isLoading
                ? 'bg-primary text-black'
                : 'bg-gray-300 text-gray-500'
                }`}
            >
              <BiSend size={20} />
            </motion.button>
          </div>
          <p className="text-xs text-center text-gray-400 mt-2">Powered by Profitsla ®</p>
        </div>
      </div>

    </>
  );
};

export default ChatInterface;